import React, { useState } from "react"
import { Link } from "react-router-dom"
import { AxiosError } from "axios"

import { Notification } from "@/components"
import ApiService from "@/services/Api"
import SuperlogicaService from "@/services/Superlogica"
import { getErrorCodeMessages } from "@/utils/response"
import { ValidationContent } from "@/hooks/useActiveCampaignApi"
import { SuperlogicaPluginSettingsData } from "@/protocols/SuperlogicaPluginProtocol"

export type SuperlogicaPluginWarningsType =
	"success" |
	"plugin-disabled" |
	"plugin-data-not-found" |
	"plugin-check-error"

type PluginWarnings = {
	[key in SuperlogicaPluginWarningsType]: ValidationContent
}

const pluginWarnings: PluginWarnings = {
	"plugin-disabled": {
		type: "warning",
		text: (
			<>
				Para que esse bloco funcione corretamente, ative o plugin da <Link to="/admin/plugins/superlogica" target="_blank">Superlógica</Link>.
			</>
		)
	},
	"plugin-data-not-found": {
		type: "warning",
		text: (
			<>
				As informações do plugin da <Link to="/admin/plugins/superlogica" target="_blank">Superlógica</Link> não estão corretas, certifique-se de que esteja tudo certo para que o bloco funcione corretamente.
			</>
		)
	},
	"plugin-check-error": {
		type: "warning",
		text: (
			<>
				Não foi possível conectar na API da <Link to="/admin/plugins/superlogica" target="_blank">Superlógica</Link>.
			</>
		)
	},
	success: {
		type: "success",
		text: (<></>)
	}
}

const useSuperlogicaApi = () => {
	const [status, setStatus] = useState<SuperlogicaPluginWarningsType>("success")

	const validateSuperlogicaApiAuth = async (): Promise<boolean> => {
		try {
			await SuperlogicaService.checkAuthCredentials()
			return true
		} catch (error) {
			const codeMessages = getErrorCodeMessages(error as AxiosError)
			if (codeMessages?.api_auth === "external_api_auth_error") {
				return false
			}
		}
	}

	const fetchPluginSettings = async (): Promise<SuperlogicaPluginSettingsData | undefined> => {
		try {
			const response = await ApiService.get("/plugin-settings", {
				params: {
					pluginType: "superlogica"
				}
			})

			return response.data.dataValues as SuperlogicaPluginSettingsData
		} catch (error) {
			Notification.error({ message: "Não foi possível carregar os dados do plugin." })
		}
	}

	const validatePluginData = (data: SuperlogicaPluginSettingsData): boolean => {
		if (data.status !== "enabled") {
			setStatus("plugin-disabled")
			return false
		}

		if (!data.settings?.app_token || !data.settings?.access_token) {
			setStatus("plugin-data-not-found")
			return false
		}

		return true
	}

	const getPluginSettings = async (): Promise<boolean> => {
		const pluginSettings = await fetchPluginSettings()

		if (!pluginSettings) {
			setStatus("plugin-disabled")
			return false
		}

		const isValidPluginData = validatePluginData(pluginSettings)

		if (!isValidPluginData) {
			return false
		}

		setStatus("success")

		return true
	}

	const validatePlugin = async (): Promise<boolean> => {
		const pluginSettings = await fetchPluginSettings()

		if (!pluginSettings) {
			setStatus("plugin-disabled")
			return false
		}

		const isValidPluginData = validatePluginData(pluginSettings)

		if (!isValidPluginData) {
			return false
		}

		const isValidApiAuth = await validateSuperlogicaApiAuth()

		if (!isValidApiAuth) {
			setStatus("plugin-check-error")
			return false
		}

		setStatus("success")

		return true
	}

	const getValidationContent = (): ValidationContent => {
		return pluginWarnings[status]
	}

	return {
		getPluginSettings,
		validatePlugin,
		getValidationContent
	}
}

export default useSuperlogicaApi
